import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2, Search, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const AssetTypesTab = ({ api, equipmentTypes, fetchEquipmentTypes }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    is_active: true,
    is_combo: false,
    combo_component_ids: []
  });

  const activeCount = equipmentTypes.filter(t => t.is_active).length;
  const customCount = equipmentTypes.filter(t => !t.is_system).length;
  const comboCount = equipmentTypes.filter(t => t.is_combo).length;

  const filteredTypes = equipmentTypes.filter(type =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setForm({ name: '', description: '', is_active: true, is_combo: false, combo_component_ids: [] });
    setEditingType(null);
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setForm({
        name: type.name,
        description: type.description || '',
        is_active: type.is_active,
        is_combo: type.is_combo || false,
        combo_component_ids: type.combo_component_ids || []
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (form.is_combo && form.combo_component_ids.length < 2) {
        toast.error('Combo types must have at least 2 component equipment types');
        return;
      }
      
      if (editingType) {
        await api.put(`/equipment-types/${editingType.id}`, form);
        toast.success('Asset type updated');
      } else {
        await api.post('/equipment-types', form);
        toast.success('Asset type created');
      }
      setDialogOpen(false);
      resetForm();
      fetchEquipmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (type) => {
    if (type.is_system) {
      toast.error('System types cannot be deleted. You can deactivate them instead.');
      return;
    }
    if (!window.confirm(`Delete equipment type "${type.name}"?`)) return;
    try {
      await api.delete(`/equipment-types/${type.id}`);
      toast.success('Asset type deleted');
      fetchEquipmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleActive = async (type) => {
    try {
      await api.put(`/equipment-types/${type.id}`, {
        name: type.name,
        is_active: !type.is_active
      });
      toast.success(`Asset type ${type.is_active ? 'deactivated' : 'activated'}`);
      fetchEquipmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const getAvailableComboComponents = () => {
    return equipmentTypes.filter(t => 
      t.is_active && 
      !t.is_combo && 
      t.id !== editingType?.id
    );
  };

  const getComboComponentNames = (componentIds) => {
    if (!componentIds || componentIds.length === 0) return '-';
    return componentIds.map(id => {
      const type = equipmentTypes.find(t => t.id === id);
      return type?.name || id;
    }).join(' + ');
  };

  return (
    <>
      <Card className="card-industrial">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Asset Types</CardTitle>
              <CardDescription>
                {activeCount} active types, {customCount} custom types{comboCount > 0 && `, ${comboCount} combo`}
              </CardDescription>
            </div>
            <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-type-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Type
            </Button>
          </div>
          <div className="relative max-w-sm mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="type-search"
              placeholder="Search types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="label-overline">Name</TableHead>
                  <TableHead className="label-overline">Type</TableHead>
                  <TableHead className="label-overline">Combo Components</TableHead>
                  <TableHead className="label-overline">Status</TableHead>
                  <TableHead className="label-overline text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTypes.map((type) => (
                  <TableRow key={type.id} data-testid={`type-row-${type.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {type.name}
                        {type.is_combo && (
                          <Badge className="bg-purple-500/20 text-purple-600 text-xs">Combo</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {type.is_system ? (
                        <Badge variant="secondary">System</Badge>
                      ) : (
                        <Badge className="bg-[#ffd108]/20 text-[#ffd108]">Custom</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[250px]">
                      {type.is_combo ? (
                        <span className="text-purple-600">
                          {getComboComponentNames(type.combo_component_ids)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch 
                        checked={type.is_active}
                        onCheckedChange={() => handleToggleActive(type)}
                        data-testid={`type-status-${type.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenDialog(type)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {!type.is_system && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => handleDelete(type)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Equipment Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingType ? 'Edit Asset Type' : 'Add Asset Type'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="type-form">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Excavator 20t"
                required
                disabled={editingType?.is_system}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_combo"
                checked={form.is_combo}
                onCheckedChange={(checked) => setForm({ 
                  ...form, 
                  is_combo: checked,
                  combo_component_ids: checked ? form.combo_component_ids : []
                })}
                disabled={editingType?.is_system}
              />
              <Label htmlFor="is_combo" className="text-sm">
                Combo type (combines multiple equipment types)
              </Label>
            </div>
            
            {form.is_combo && (
              <div className="space-y-2">
                <Label>Component Equipment Types</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select at least 2 equipment types that make up this combo
                </p>
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {getAvailableComboComponents().map(type => (
                    <div key={type.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`combo-${type.id}`}
                        checked={form.combo_component_ids.includes(type.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setForm({ 
                              ...form, 
                              combo_component_ids: [...form.combo_component_ids, type.id] 
                            });
                          } else {
                            setForm({ 
                              ...form, 
                              combo_component_ids: form.combo_component_ids.filter(id => id !== type.id) 
                            });
                          }
                        }}
                      />
                      <Label htmlFor={`combo-${type.id}`} className="text-sm font-normal">
                        {type.name}
                      </Label>
                    </div>
                  ))}
                </div>
                {form.combo_component_ids.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Selected: {getComboComponentNames(form.combo_component_ids)}
                  </p>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt">
                {editingType ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssetTypesTab;
